import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import {
  BankReconciliationService,
  Currency,
  TransactionSource,
  UserDailyCollection,
} from '../../../service/bank-reconciliation.service';

@Component({
  selector: 'app-sales-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, ToastModule, ButtonModule, DropdownModule,
    InputTextModule, InputNumberModule, CalendarModule, TableModule, TagModule],
  templateUrl: './sales-upload.component.html',
  styleUrl: './sales-upload.component.scss',
  providers: [MessageService, DatePipe]
})
export class SalesUploadComponent implements OnInit {

  private readonly service = inject(BankReconciliationService);
  private readonly datePipe = inject(DatePipe);
  messageService = inject(MessageService);

  currencies = signal<Currency[]>([]);
  history = signal<UserDailyCollection[]>([]);

  transactionSources: { label: string; value: TransactionSource }[] = [
    { label: 'POS', value: 'POS' },
    { label: 'Mobile Money', value: 'MOBILE_MONEY' },
    { label: 'Bank Deposit', value: 'BANK_DEPOSIT' },
    { label: 'Bank Statement', value: 'BANK_STATEMENT' },
    { label: 'Cash Collection', value: 'CASH_COLLECTION' },
  ];

  transactionDate: Date | null = null;
  transactionSource: TransactionSource | null = null;
  description: string = '';
  reference: string = '';
  paymentAmount: number | null = null;
  currencyId: number | null = null;

  today = new Date();

  submitting = signal(false);
  loadingCurrencies = signal(false);
  loadingHistory = signal(false);

  ngOnInit(): void {
    this.loadCurrencies();
    this.loadHistory();
  }

  private loadCurrencies(): void {
    this.loadingCurrencies.set(true);
    this.service.getCurrencies().subscribe({
      next: (res) => {
        this.currencies.set(res.currencies ?? []);
        this.loadingCurrencies.set(false);
      },
      error: () => this.loadingCurrencies.set(false),
    });
  }

  loadHistory(): void {
    this.loadingHistory.set(true);
    this.service.getAgentSales().subscribe({
      next: (res) => {
        this.history.set(res.userDailyCollections ?? []);
        this.loadingHistory.set(false);
      },
      error: () => this.loadingHistory.set(false),
    });
  }

  isFormValid(): boolean {
    return !!(this.transactionDate && this.transactionSource && this.paymentAmount && this.currencyId);
  }

  submit(): void {
    if (!this.isFormValid()) return;
    this.submitting.set(true);
    this.service.createAgentSale({
      transactionDate: this.datePipe.transform(this.transactionDate, 'yyyy-MM-dd')!,
      transactionSource: this.transactionSource!,
      description: this.description,
      reference: this.reference,
      paymentAmount: this.paymentAmount!,
      currencyId: this.currencyId!,
    }).subscribe({
      next: (res: any) => {
        this.submitting.set(false);
        const result = Array.isArray(res) ? res[0] : res;
        const newRecord: UserDailyCollection = result?.userDailyCollection;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: result?.message ?? 'Sale recorded successfully' });
        if (newRecord) {
          this.history.update(h => [newRecord, ...h]);
        } else {
          this.loadHistory();
        }
        this.resetForm();
      },
      error: (err: any) => {
        this.submitting.set(false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message ?? 'Failed to record sale' });
      },
    });
  }

  private resetForm(): void {
    this.transactionDate = null;
    this.transactionSource = null;
    this.description = '';
    this.reference = '';
    this.paymentAmount = null;
    this.currencyId = null;
  }

  getSourceSeverity(source: string): 'success' | 'info' | 'warn' | 'danger' {
    switch (source) {
      case 'POS': return 'success';
      case 'MOBILE_MONEY': return 'info';
      case 'BANK_DEPOSIT': return 'info';
      case 'BANK_STATEMENT': return 'warn';
      default: return 'info';
    }
  }
}
